/**
 * sliding-calculator — port of the prod (banescousa.com) interaction.
 *
 * Behavior:
 * - On `change` / `mousemove` / `input` of the range slider, compute a
 *   percent from min/max/value, write it to `--range-percent`, update
 *   the floating indicator text (formatted relationship balance), and
 *   mark which tier column is active.
 * - On click of a `.selectors button`, snap the slider to that
 *   button's `data-value` and re-run the same logic — used by the
 *   mobile-only `.selectors.no-desktop` tier picker since touch can't
 *   trigger mousemove on a range.
 * - The CSS dim-non-active rule (`.row:has(> .is-active) > :not(.is-active)`)
 *   reads the .is-active class JS sets here.
 *
 * Tier-column resolution mirrors prod: tier 1 ($100k–$249,999.99) and
 * tier 2 (≥$250k) thresholds; column index 0/1/2 → DOM nth-child 2/3/4.
 */

function debounce(func, delay) {
    let timer = null;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

function setupCalculator(section) {
    const range = section.querySelector('input[type="range"]');
    const selectors = section.querySelectorAll('div.selectors button');
    const indicator = section.querySelector('#indicator, [id^="indicator"]');
    const currentLanguage = document.documentElement.lang || 'en';

    if (!range) return;

    const setScroll = debounce(function (percent) {
        const grid = section.querySelector('div.grid-wrap');
        if (!grid) return;
        const scrollLeft = parseInt(percent * (grid.scrollWidth - grid.clientWidth) / 100, 10);
        if (window.innerWidth <= 768) {
            if (Math.ceil(grid.scrollLeft) !== scrollLeft) {
                grid.scrollLeft = scrollLeft;
            }
        }
    }, 250);

    const setColumn = debounce(function (percent, priceDifference) {
        const column1Limit = 100000;
        const column2Limit = 250000;
        let index;

        if (priceDifference >= column1Limit && priceDifference < column2Limit) {
            index = 1;
        } else if (priceDifference >= column2Limit) {
            index = 2;
        } else {
            index = 0;
        }
        // DOM nth-child is 1-based and the first column is the row label,
        // so the active tier column is at nth-child(index + 2).
        index = index + 2;

        const columns = section.querySelectorAll('div.row > *');
        const selected = section.querySelectorAll(`div.row > :nth-child(${index})`);

        columns.forEach((c) => c.classList.remove('is-active'));
        selected.forEach((c) => c.classList.add('is-active'));
    }, 50);

    const getRange = function (e) {
        const target = e.target;
        const percent = (target.value - target.min) / (target.max - target.min) * 100;

        // Read $ thresholds from the surrounding labels' data-value attrs.
        const range_div = target.closest('.range') || section;
        const labels = range_div.querySelectorAll('label[data-value]');
        const values = [];
        labels.forEach((l) => values.push(parseInt(l.getAttribute('data-value'), 10)));

        const wrapper = target.closest('.range-input') || section;
        wrapper.style.setProperty('--range-percent', percent + '%');

        const min = values.length ? Math.min(...values) : 0;
        const max = values.length ? Math.max(...values) : 100;
        const priceDifference = (max - min) * percent / 100 + min;

        if (indicator) {
            const symbol = currentLanguage === 'pt' ? 'US$' : '$';
            indicator.textContent = `${symbol}${new Intl.NumberFormat().format(priceDifference)}`;
        }
        target.setAttribute(
            'aria-valuetext',
            `${currentLanguage === 'pt' ? 'US$' : '$'}${new Intl.NumberFormat().format(priceDifference)}`
        );

        setScroll(percent);
        setColumn(percent, priceDifference);

        // Mirror the active state into the selector pills.
        selectors.forEach((sel) => {
            if (parseInt(sel.getAttribute('data-value'), 10) === percent) {
                sel.classList.add('is-active');
            } else {
                sel.classList.remove('is-active');
            }
        });
    };

    const setSelectors = function (e) {
        selectors.forEach((sel) => sel.classList.remove('is-active'));
        e.currentTarget.classList.add('is-active');
        range.value = e.currentTarget.getAttribute('data-value');
        getRange({ target: range });
    };

    range.addEventListener('change', getRange);
    range.addEventListener('mousemove', getRange);
    range.addEventListener('input', getRange);

    selectors.forEach((sel) => sel.addEventListener('click', setSelectors));

    // No initial getRange() call — the slider is hidden site-wide and
    // desktop hover-on-column is driven by pure CSS :has() rules. Auto-
    // running getRange() at init would tag column 2 as .is-active for
    // value=0, which makes the dim rule (CSS) fade columns 3 and 4
    // permanently before the user interacts. On mobile the .selectors
    // pill click runs getRange via setSelectors as needed.
}

export function init() {
    document
        .querySelectorAll('section[id^="sliding_calculator"]')
        .forEach(setupCalculator);
}
